import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Button, IconButton, Link as TableLink } from '@mui/material';
import { intersectionBy, isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterList } from 'src/components/filterList';
import {
  AssignLocationsModal,
  PreviewLocationGroupsModal,
  SimpleDialog,
} from 'src/components/modal';
import { Snackbar } from 'src/components/snackbar';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import MainPane from 'src/components/TwoPaneLayout/MainPane';
import SidePane from 'src/components/TwoPaneLayout/SidePane';
import TwoPaneLayout from 'src/components/TwoPaneLayout/TwoPaneLayout';
import { PATHS } from 'src/constants/paths';
import { retainScheduleDaypartOverrides } from 'src/features/locations/listLocationsPage/helpers/scheduleDaypartOverrides.helper';
import {
  useFilterAndSorting,
  useUpdateLocations,
  useViewLocationsByGroupsWithoutPaginationFilterByLocationCode,
} from 'src/hooks';
import { useDeleteScheduleLocationExclusions } from 'src/hooks/api/schedules/scheduleLocation/useDeleteScheduleLocationExclusions';
import { useSearchFilter } from 'src/hooks/useSearchFilter';
import { IFilter, ILocation, IViewLocationGroupList } from 'src/interfaces';
import { escapeSearchTerms, lang, useUserRole } from 'src/utils';

import { useLocationTab } from '../../hooks/useLocationTab.hook';
import { ActionsContainer } from '../../LocationListPage.styled';

export const UnassignedLocationTab = () => {
  const {
    locations,
    isLocationTabDataLoading,
    totalCount,
    totalPages,
    activeFilters,
    handlePageChange,
    onSortChangeHandle,
    selectedSortOption,
    page,
    assignLocationGroupModal,
    confirmLocationGroupModal,
    previewLocationGroupsModal,
    getParamsValue,
    rowsPerPage,
  } = useLocationTab();
  const deleteScheduleLocationExclusions =
    useDeleteScheduleLocationExclusions();
  const { handleOnChangeFilterKeyword, handleOnRemoveFilterKeyword } =
    useSearchFilter();
  const [isStoreNumberFuzzySearch, setIsStoreNumberFuzzySearch] = useState(
    !('store_number.list' in activeFilters),
  );

  const onStoreNumberSearchTypeChange = () => {
    if (isStoreNumberFuzzySearch) {
      setIsStoreNumberFuzzySearch(false);
      onChangeToExactMatch('store_number.regexp', 'store_number.list');
    } else {
      setIsStoreNumberFuzzySearch(true);
      onChangeToFuzzyMatch('store_number.list', 'store_number.regexp');
    }
  };
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const updateLocations = useUpdateLocations();
  const [selectedLocation, setSelectedLocation] = useState<any>();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedLocationGroups, setSelectedLocationGroups] =
    useState<IViewLocationGroupList[]>();
  const { data: viewLocationGroup, isLoading: isLocationGroupsLoading } =
    useViewLocationsByGroupsWithoutPaginationFilterByLocationCode(
      null,
      { enabled: selectedLocation?.location_code !== undefined },
      [`locations.location_code=${selectedLocation?.location_code}`],
    );

  const onSaveLocationGroups = (
    selectedLocationGroups: IViewLocationGroupList[],
  ) => {
    setSelectedLocationGroups(selectedLocationGroups);

    assignLocationGroupModal.onCloseModal();
    confirmLocationGroupModal.onOpenModal();
  };

  const onHandleSaved = async () => {
    try {
      setIsSaving(true);
      const payload = {
        location_code: selectedLocation?.location_code ?? '',
        location_group_id: selectedLocationGroups?.map((item) => item.id),
      } as ILocation;

      const filteredLocationGroups = selectedLocationGroups?.filter(
        (locationGroup) =>
          !locationGroup.locations.some(
            (location) =>
              location.location_code === selectedLocation?.location_code,
          ),
      );

      const oldLocationGroups = viewLocationGroup?.locationGroups.filter(
        (group) =>
          group.locations.some(
            (location) =>
              location.location_code === selectedLocation?.location_code,
          ),
      );

      // * shared functionality between DetailLocationPage.tsx, UnassignedLocationTab.tsx, AssignLocationGroupPage.hook.ts
      await retainScheduleDaypartOverrides(
        selectedLocation?.location_code,
        filteredLocationGroups ?? [],
        oldLocationGroups,
      );

      // cleanup schedules for old location groups (no exclusions for defaults)
      if (filteredLocationGroups?.length) {
        const previousLocationGroups = intersectionBy(
          oldLocationGroups,
          filteredLocationGroups,
          'channel_name',
        ).flatMap((locationGroup) =>
          !locationGroup.is_default ? locationGroup.id : [],
        );

        if (previousLocationGroups.length) {
          const allLocationGroupsUpdated =
            previousLocationGroups.length ===
            selectedLocation?.channel_id?.length;

          await deleteScheduleLocationExclusions.mutateAsync({
            locationCode: selectedLocation?.location_code,
            ...(!allLocationGroupsUpdated && { previousLocationGroups }),
          });
        }
      }

      await updateLocations.mutateAsync([payload]);
      Snackbar.success(lang.successfullyAddedLocation);
      confirmLocationGroupModal.onCloseModal();
      previewLocationGroupsModal.onOpenModal();
    } catch (error) {
      Snackbar.error(lang.failedToAddLocation);
    } finally {
      setIsSaving(false);
    }
  };

  const filters: IFilter[] = [
    {
      label: lang.locationTagsName,
      labelOverride: lang.locationTags,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'primary',
      },
      customElement: (
        <TableLink
          component="button"
          variant="body2"
          onClick={() => navigate(PATHS.LOCATIONS_TAGS)}>
          {lang.manageTags}
        </TableLink>
      ),
    },
    {
      labelOverride: lang.ownerOperator.ownerOperatorTags,
      label: lang.locationOwnerTagsName,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'success',
      },
    },
    {
      label: lang.zone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      label: lang.subZone,
      options: [],
      isLoading: false,
      fieldType: 'filterWithSearch',
      badgeOptions: {
        align: 'top',
        color: 'default',
      },
    },
    {
      labelOverride: 'Store number',
      label: 'store_number',
      isLoading: false,
      options: [],
      fieldType: 'freeSearch',
      hasExactSearch: true,
      isFuzzySearch: isStoreNumberFuzzySearch,
      onSearchTypeChange: onStoreNumberSearchTypeChange,
      filterSearchVal: getParamsValue(
        isStoreNumberFuzzySearch ? 'store_number.regexp' : 'store_number.list',
      ),
      onChangeFilterKeyword: (value: string) =>
        handleOnChangeFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
      onRemoveFilterKeyword: (value: string) =>
        handleOnRemoveFilterKeyword(
          value,
          isStoreNumberFuzzySearch
            ? 'store_number.regexp'
            : 'store_number.list',
        ),
    },
  ];

  const [isFuzzySearch, setIsFuzzySearch] = useState(
    !('name.list' in activeFilters),
  );
  const {
    onChangeKeyword,
    onRemoveKeyword,
    onChangeFilter,
    onChangeToExactMatch,
    onChangeToFuzzyMatch,
    onReset,
    onChangeParams,
  } = useFilterAndSorting(filters, [], isFuzzySearch, setIsFuzzySearch);

  const onResetFilters = () => {
    onReset();
    onChangeParams('store_number.list', []);
    onChangeParams('store_number.regexp', []);
    setIsStoreNumberFuzzySearch(true);
  };

  const onSearchTypeChange = () => {
    if (isFuzzySearch) {
      onChangeToExactMatch('name.regexp', 'name.list');
      setIsFuzzySearch(false);
    } else {
      onChangeToFuzzyMatch('name.list', 'name.regexp');
      setIsFuzzySearch(true);
    }
  };

  const onHandleChangeKeyword = (value: string | string[]) => {
    if (!isFuzzySearch) {
      onChangeKeyword(value);

      return;
    }

    const escaped = escapeSearchTerms(value, getParamsValue('name.regexp'));
    // we should avoid the terms re-escaping
    onChangeParams('name.regexp', [...new Set(escaped)] as string[]);
  };

  return (
    <TwoPaneLayout>
      <SidePane>
        <FilterList
          enableSearchArea
          filters={filters}
          onChangeFilter={onChangeFilter}
          onRemoveKeyword={onRemoveKeyword}
          onChangeKeyword={onHandleChangeKeyword}
          onReset={onResetFilters}
          showReset
          hasExactSearch
          onSearchTypeChange={onSearchTypeChange}
          isFuzzySearch={isFuzzySearch}
        />
      </SidePane>

      <MainPane>
        <CustomTableWithPagination
          columns={[
            { id: 'name', label: lang.locationName },
            { id: 'store_number', label: lang.storeNumber },
            { id: 'total_channels', label: lang.totalChannels },
            { id: 'unassigned_channels', label: lang.unassignedChannels },
            { id: 'actions', label: '' },
          ]}
          onSortChange={onSortChangeHandle}
          selectedSortOption={selectedSortOption}
          rows={locations}
          loading={isLocationTabDataLoading}
          renderActions={(row) => (
            <ActionsContainer>
              {!isViewOnly && (
                <Button
                  variant="outlined"
                  sx={{ minWidth: '190px' }}
                  onClick={() => {
                    setSelectedLocation(row);
                    assignLocationGroupModal.onOpenModal();
                  }}>
                  {lang.assignToLocationGroup}
                </Button>
              )}
              <CustomTooltip
                title={lang.locationDetails}
                placement="top"
                skipValidateOverflow={true}>
                <IconButton
                  aria-label="clock"
                  onClick={() =>
                    navigate({
                      pathname: PATHS.LOCATION_DETAILS(
                        row.location_code.toString(),
                      ),
                    })
                  }>
                  <InfoOutlinedIcon />
                </IconButton>
              </CustomTooltip>
            </ActionsContainer>
          )}
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          showTableControls
          onPageChange={handlePageChange}
        />
      </MainPane>

      {assignLocationGroupModal.open && !isEmpty(selectedLocation) && (
        <AssignLocationsModal
          open={assignLocationGroupModal.open}
          onClose={assignLocationGroupModal.onCloseModal}
          onClickConfirm={onSaveLocationGroups}
          isLocationGroupsLoading={isLocationGroupsLoading}
          locationCode={selectedLocation?.location_code}
        />
      )}
      {confirmLocationGroupModal.open && (
        <SimpleDialog
          open={confirmLocationGroupModal.open}
          cancelText={lang.cancel}
          confirmText={lang.yesConfirm}
          title={lang.confirmLocationGroupModal.title}
          subTitle={lang.confirmLocationGroupModal.subTitle}
          onClose={confirmLocationGroupModal.onCloseModal}
          onClickCancel={confirmLocationGroupModal.onCloseModal}
          isLoading={isSaving}
          disabled={isSaving}
          modalSize="sm"
          onClickConfirm={onHandleSaved}
        />
      )}
      {previewLocationGroupsModal.open && (
        <PreviewLocationGroupsModal
          open={previewLocationGroupsModal.open}
          onClose={previewLocationGroupsModal.onCloseModal}
          title={lang.previewLocationGroupsModal.title}
          locationName={selectedLocation?.name ?? ''}
          selectedLocationGroups={selectedLocationGroups ?? []}
        />
      )}
    </TwoPaneLayout>
  );
};
